import type { AppFeatures } from "@glide/app-description";
import { getFeatureSetting } from "@glide/common-core";
import { defaultAppFeatures } from "@glide/common-core/dist/js/components/SerializedApp";
import type { GetAppLockoutStateResponse } from "@glide/common-core/dist/js/firebase-function-types";
import { getAppLockoutStateResponse } from "@glide/common-core/dist/js/firebase-function-types";
import { getAppFacilities } from "@glide/common-core/dist/js/support/app-renderer";
import { useAppID } from "@glide/common-core/dist/js/use-app-id";
import { isResponseOK } from "@glide/support";
import { defined } from "@glideapps/ts-necessities";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { isLeft } from "fp-ts/lib/Either";

function useGetAppLockoutState(): UseQueryResult<GetAppLockoutStateResponse, Error> {
    const appID = defined(useAppID());
    return useQuery({
        queryKey: ["get-app-lockout-state", appID],
        queryFn: async () => {
            const appFacilities = getAppFacilities();
            const response = await appFacilities.callCloudFunction("getAppLockoutState", { appID }, {});
            if (!isResponseOK(response)) {
                throw new Error("Error with getAppLockoutState response");
            }

            const responseBody = await response.json();
            const decodedResponseBody = getAppLockoutStateResponse.decode(responseBody);
            if (isLeft(decodedResponseBody)) {
                throw new Error("Error decoding getAppLockoutState response");
            }

            return decodedResponseBody.right;
        },
        enabled: getFeatureSetting("enforcePlayerLockoutViaDedicatedEndpoint"),
        refetchOnWindowFocus: false,
        refetchInterval: 5 * 60 * 1_000,
    });
}

export function useIsAppLocked(maybeAppFeatures: AppFeatures | undefined): boolean {
    const { data, status } = useGetAppLockoutState();

    if (getFeatureSetting("enforcePlayerLockoutViaDedicatedEndpoint")) {
        return status === "success" ? data.isAppLocked : false;
    }

    const appFeatures = maybeAppFeatures ?? defaultAppFeatures;
    return appFeatures.showLockout;
}
